import { Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { useState, useEffect, useRef } from 'react'
import { ErrorTemplateFieldDto, TemplateFieldDto, TemplateModelDto } from "../types";
import { ApiErrorResponse } from "../../../../types/ApiErrorResponse";
import { useTranslation } from "react-i18next";
import { createTemplate, getTemplateById, getTemplateFieldsFromExcel, updateTemplate } from "../api/templateAPI";
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import "react-datepicker/dist/react-datepicker.css";
import TemplateField from "../components/TemplateField";
import { convertUtc } from "../../../../utils/dateUtils"
import { InputDatePicker } from "../../../../components/InputDatePicker";
import { getTemplateFormSchema } from "../schema/templateSchema";
import { ExtraFieldObjectTypeEnum } from "../../surveyImportExtraFields/types/enums";
import ModalGlobal from "../../../../components/AppModal/ModalGlobal";
import { SurveyImportExtraFieldFormDto } from "../../surveyImportExtraFields/types";
import { getAllExtraFields, getHistoricalOrderSelectOptions } from "../../surveyImportExtraFields/api/extraFieldApi";
import { SelectItemDto } from "../../../../types/SelectItemDto";
import { GenericSaveButton } from "../../../../components/common/GenericSaveButton";

type TemplateFormErrors = {
    description?: string;
    effectiveStartDate?: string;
    effectiveEndDate?: string;
    fields?: ErrorTemplateFieldDto[];
    api?: ApiErrorResponse;
};

export default function TemplateForm() {
    const location = useLocation();
    const { t } = useTranslation('template');
    const navigate = useNavigate();
    const { idTemplate } = useParams<{ idTemplate: string }>();
    const isClone = location.state?.clone;

    const [template, setTemplate] = useState<TemplateModelDto>(
        isClone ?
            location.state?.template :
            {
                id: 0,
                description: "",
                effectiveEndDate: null,
                effectiveStartDate: null,
                fields: []
            }
    );
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    
    const [extraFieldOptions, setExtraFieldOptions] = useState<SurveyImportExtraFieldFormDto[]>([]);
    const [historicalOrderOptions, setHistoricalOrderOptions] = useState<SelectItemDto[]>([]);
    const [errors, setErrors] = useState<TemplateFormErrors>({});
    const [loading, setLoading] = useState(false);
    const [showModalMessageFieldAnwerDate, setShowModalMessageFieldAnwerDate] = useState(false);

    useEffect(() => {
        getAllExtraFields()
            .then(setExtraFieldOptions)
            .catch((err: ApiErrorResponse) => {
                setErrors(prev => ({
                    ...prev,
                    api: err
                }));
            });
        getHistoricalOrderSelectOptions()
            .then(setHistoricalOrderOptions)
            .catch((err: ApiErrorResponse) => {
                setErrors(prev => ({
                    ...prev,
                    api: err
                }));
            });

        if (!idTemplate) 
            return;

        setLoading(true);
        getTemplateById(idTemplate)
            .then(data => {
                setTemplate(data);

                const destinationFields: number[] = [];
                data.fields.forEach((f) => {
                    if (f.objectType) {
                        destinationFields.push(f.extraFieldId);
                    }
                });
            })
            .catch((err: ApiErrorResponse) => {
                setErrors(prev => ({
                    ...prev,
                    api: err
                }));
            })
            .finally(() => setLoading(false));       
    }, [idTemplate]);

    const handleChangeData = (field: keyof TemplateModelDto, value: TemplateModelDto[keyof TemplateModelDto]) => {
        setTemplate(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleEffectiveStartDate = (date: Date | null) => {
        const utcDate = convertUtc(date);
        handleChangeData('effectiveStartDate', utcDate ? utcDate.getTime() : null)

        setErrors(prev => ({
            ...prev,
            effectiveStartDate: undefined,
            effectiveEndDate: undefined,
        }));
    }

    const handleEffectiveEndDate = (date: Date | null) => {
        const utcDate = convertUtc(date);
        handleChangeData('effectiveEndDate', utcDate ? utcDate.getTime() : null)

        setErrors(prev => ({
            ...prev,
            effectiveStartDate: undefined,
            effectiveEndDate: undefined,
        }));
    }

    const handleSaveTemplate = async () => {
        setLoading(true);
        const schema = getTemplateFormSchema(t);
        const newErrors: TemplateFormErrors = {};

        try {
            await schema.validate(template, { abortEarly: false });
        } catch (err: any) {
            if (err?.inner && Array.isArray(err.inner) && err.inner.length > 0) {
                err.inner.forEach((error: any) => {
                    if (!error.path) return;

                    const fieldMatch = /^fields\[(\d+)\]\.(.+)$/.exec(error.path);
                    if (fieldMatch) {
                        const index = Number(fieldMatch[1]);
                        const fieldName = fieldMatch[2] as keyof ErrorTemplateFieldDto;
                        newErrors.fields ??= [];
                        newErrors.fields[index] ??= {};
                        newErrors.fields[index][fieldName] = error.message;
                        return;
                    }

                    if (error.path in newErrors || ["description", "effectiveStartDate", "effectiveEndDate"].includes(error.path)) {
                        (newErrors as any)[error.path] = error.message;
                    }
                });
            } else if (err?.path) {
                (newErrors as any)[err.path] = err.message;
            }

            setErrors(newErrors);
            setLoading(false);
            return;
        }

        //Comprobamos si no existe ningun campo AnwerDate definido
        const exists = template.fields.some(f => extraFieldOptions.find(option => option.id == f.extraFieldId)?.fieldName == "AnswerDate");
        if (exists) {
            saveTemplate();
        } else {
            setLoading(false);
            setShowModalMessageFieldAnwerDate(true);
        }
    };

    const saveTemplate = async  () => {
        try {
            let message;
            setLoading(true);

            if (idTemplate) {
                await updateTemplate(idTemplate!, template);
                message = t("template:edit.messageUpdatedSuccessfully");
            }
            else {
                await createTemplate(template);
                message = t("template:edit.messageCreatedSuccessfully");
            }

            setLoading(false);
            navigate('/admin/template', { state: { message: message } });

        } catch (error) {
            const fetchError = error as ApiErrorResponse;
            setErrors({
                api: fetchError
                    ? {
                        code: (fetchError as ApiErrorResponse).code,
                        message: (fetchError as ApiErrorResponse).message
                    }
                    : { code: "0", message: t("common:systemError") }
            });
            setLoading(false);
        }
    }

    const handleOpenTemplateFileSelector = () => {
        fileInputRef.current?.click();
    };

    const handleTemplateFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (!selectedFile) {
            return;
        }

        setLoading(true);
        try {
            const newFields: TemplateFieldDto[] = (await getTemplateFieldsFromExcel(selectedFile)).map(field => ({
                ...field,
                uid: crypto.randomUUID()
            }));
            handleChangeData('fields', [...template.fields, ...newFields]);
        } catch (error) {
            const fetchError = error as ApiErrorResponse;
            setErrors({
                api: fetchError
                    ? {
                        code: fetchError.code,
                        message: fetchError.message
                    }
                    : { code: "0", message: t("common:systemError") }
            });
        } finally {
            e.target.value = "";
            setLoading(false);
        }
    };

    const handleAddField = () => {
        const newField: TemplateFieldDto = {
            uid: crypto.randomUUID(),
            id: 0,
            order: null,
            originFieldName: "",
            extraFieldId: -1,
            description: "",
            isFilterable: false,
            showInHistorical: false,
            objectType: ExtraFieldObjectTypeEnum.Info,
        };
        handleChangeData('fields', [...template.fields, newField]);
    };

    const handleSaveForm = (continueSave: boolean) => {
        setShowModalMessageFieldAnwerDate(false)
        if (continueSave) {
            saveTemplate();
        } 
    }

    return (
        <>
            {loading && (
                <div className="overlay-spinner">
                    <Spinner animation="border" variant="light" />
                </div>
            )}
            <ModalGlobal
                show={showModalMessageFieldAnwerDate}
                onClose={() => handleSaveForm(false)}
                body={t("template:edit.modalAnswerDateValidationTitle")}
                onSave={() => {
                    handleSaveForm(true);
                }}
                saveText={t("common:buttons.buttonYes")}
                closeText={t("common:buttons.buttonNo")}
                isLoading={false}
            />
            <Row>
                <Col>
                    <Form>
                        <Row>
                            <Col xs={12} md={8}>
                                <h2>{t("template:edit.titleEditTemplate")}</h2>
                            </Col>
                            <Col xs={12} md={4} className="text-end mt-2 mt-md-0">
                                <GenericSaveButton
                                    onClick={() => handleSaveTemplate()}
                                    onCancelClick={() => navigate('/admin/template')}
                                    cancelLabel={t("common:buttons.buttonCancel")}
                                    saveLabel={loading ? t("common:buttons.buttonSaveingProgress") : t("common:buttons.buttonSave")}
                                    disabled={loading}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col xs={12} md={12}>
                                <Form.Group>
                                    <Form.Label>{t("template:edit.newTemplateInputTextDecription")} </Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={template.description}
                                        isInvalid={!!errors.description}
                                        onChange={(e) => handleChangeData('description', e.target.value)}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.description}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row style={{ marginTop: '10px' }}>
                            <Col xs={6} md={6}>
                                <InputDatePicker
                                    value={template.effectiveStartDate ? new Date(template.effectiveStartDate) : null}
                                    onChange={handleEffectiveStartDate}
                                    hasError={!!errors.effectiveStartDate}
                                    errorMessage={errors.effectiveStartDate}
                                    label={t("template:edit.newTemplateInputTextEffectiveStartDate")}
                                />                                    
                            </Col>
                            <Col xs={6} md={6}>
                                <InputDatePicker
                                    value={template.effectiveEndDate ? new Date(template.effectiveEndDate) : null}
                                    onChange={handleEffectiveEndDate}
                                    hasError={!!errors.effectiveEndDate}
                                    errorMessage={errors.effectiveEndDate}
                                    label={t("template:edit.newTemplateInputTextEffectiveEndDate")}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col xs={12} md={12} style={{ marginTop: '40px' }}>
                            {errors.api?.message && (
                                <div className="alert alert-danger">
                                        {errors.api.message}
                                        <button
                                            type="button"
                                            className="btn-close btn-sm position-absolute top-0 end-0 m-2"
                                            aria-label="Close"
                                            onClick={() => setErrors({})}
                                        ></button>
                                </div>
                            )}
                            </Col>
                        </Row>
                        <Row style={{ marginTop: '40px' }}>
                            <h5>{t("template:newTemplateHeaderFields")}</h5>
                        </Row>

                        {template.fields.map((field, index) => (
                            <Row key={field.uid}>
                                <TemplateField
                                    key={field.uid}
                                    currentField={field}
                                    indexField={index}
                                    fields={template.fields}
                                    extraFieldOptions={extraFieldOptions}
                                    orderOptions={historicalOrderOptions}
                                    setFields={(fields) => handleChangeData('fields', [...fields])}
                                    showPlus={index === template.fields.length - 1} 
                                    errorsField={errors.fields ? errors.fields[index] : undefined}
                                />
                            </Row>
                        ))}
                        <Row className="mt-3">
                            <Col xs={12} className="d-flex justify-content-end">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".xlsx,.xls"
                                    style={{ display: "none" }}
                                    onChange={handleTemplateFileSelected}
                                />
                                <Button
                                    onClick={handleOpenTemplateFileSelector}
                                    variant="secondary"
                                    className="me-2"
                                >
                                    {t("common:csv.selectFileButton")}
                                </Button>
                                <Button
                                    onClick={handleAddField}
                                    variant="primary"
                                >
                                    {t("template:newTemplateButtonAddNewField")}
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                </Col>
            </Row>
        </>
    );
}

